"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CalendarDate = exports.createCalendarDateValidator = exports.isCalendarDate = void 0;
var date_fns_1 = require("date-fns");
var date_fns_tz_1 = require("date-fns-tz");
var isDate_1 = __importDefault(require("lodash/isDate"));
var isObject_1 = __importDefault(require("lodash/isObject"));
var formats_1 = require("./formats");
var utils_1 = require("./utils");
/**
 * Check to see if a value is CalendarDate
 */
function isCalendarDate(date) {
    return (0, isObject_1.default)(date) && 'value' in date && (0, utils_1.isDateString)(date.value);
}
exports.isCalendarDate = isCalendarDate;
/**
 * Convert a date object into a date string in the form yyyy-MM-dd.
 */
function dateToDateString(date, timezone) {
    return (0, date_fns_tz_1.formatInTimeZone)(date, timezone, 'yyyy-MM-dd');
}
/**
 * Convert a CalendarDateObject into a date string in the form yyyy-MM-dd.
 */
function calendarDateObjectToDateString(obj) {
    return "".concat(obj.year, "-").concat((0, utils_1.prependWithZero)(obj.month), "-").concat((0, utils_1.prependWithZero)(obj.day));
}
/**
 * This can be passed to a yup scheme to validate that a form field is a valid
 * date string given the constraints.
 *
 *    const schema = yup().object({
 *      date: yup().string().test('isValidDate', '${path} must be a valid date string after 1st Jan 2020 and not on a weekend', validateDateString({
 *        allowWeekends: false,
 *        minDate: '2020-01-01',
 *      }))
 *    });
 */
function createCalendarDateValidator(options) {
    return function (value) {
        var calendarDate = new CalendarDate(value);
        return calendarDate.check(options);
    };
}
exports.createCalendarDateValidator = createCalendarDateValidator;
/**
 * This represents a single calendar date without a time. This should be used when
 * you're working with dates.
 */
var CalendarDate = /** @class */ (function () {
    function CalendarDate(value) {
        if (!(0, utils_1.isDateString)(value)) {
            throw new Error('Invalid date passed to CalendarDate constructor. Must be a DateString in the format yyyy-mm-dd.');
        }
        this.value = value;
    }
    /**
     * Takes any user input and parses a date from it.
     */
    CalendarDate.fromString = function (str, format) {
        try {
            var date = (0, date_fns_1.parse)(str, format, new Date());
            if (!(0, date_fns_1.isValid)(date)) {
                return null;
            }
            // `parse` will always parse the date from the string in the users local timezone.
            // So we need to create the calendar date from local time. Otherwise the calendar
            // date could be on the wrong date.
            return CalendarDate.fromDate(date, (0, utils_1.getLocalTimezone)());
        }
        catch (e) {
            return null;
        }
    };
    /**
     * Creates a CalendarDate from another date-like value
     */
    CalendarDate.fromCalendarDate = function (initialValue) {
        if (isCalendarDate(initialValue)) {
            return initialValue;
        }
        if ((0, utils_1.isCalendarDateObject)(initialValue)) {
            return new CalendarDate(calendarDateObjectToDateString(initialValue));
        }
        if ((0, utils_1.isDateString)(initialValue)) {
            return new CalendarDate(initialValue);
        }
        throw new Error(
        // eslint-disable-next-line max-len
        'Invalid date passed to CalendarDate constructor. Must be a CalendarDate, CalendarDateObject, or DateString');
    };
    /**
     * Creates a CalendarDate from a native date type, either a Date or ISO string. The timezone is required.
     */
    CalendarDate.fromDate = function (initialValue, timezone) {
        if (timezone === void 0) { timezone = 'UTC'; }
        if ((0, isDate_1.default)(initialValue)) {
            if (!timezone) {
                throw new Error('Date passed to CalendarDate constructor without the timezone option');
            }
            return new CalendarDate(dateToDateString(initialValue, timezone));
        }
        if ((0, utils_1.isISOString)(initialValue)) {
            if (!timezone) {
                throw new Error('ISODateString passed to CalendarDate constructor without the timezone option');
            }
            return new CalendarDate(dateToDateString(new Date(initialValue), timezone));
        }
        throw new Error(
        // eslint-disable-next-line max-len
        'Invalid date passed to CalendarDate constructor. Must be a Date or ISODateString');
    };
    /**
     * When parsing dates from the API, often the date is optional. This means that the developer needs to write
     * a guard for undefined every time. This method will do the same as fromDate but it will handle undefined.
     * @param initialValue
     * @param timezone
     * @returns
     */
    CalendarDate.fromDateOrUndefined = function (initialValue, timezone) {
        if (typeof initialValue === 'undefined') {
            return undefined;
        }
        return CalendarDate.fromDate(initialValue, timezone);
    };
    /**
     * Create a CalendarDate for today, relative to the timezone of the client.
     * @returns CalendarDate
     */
    CalendarDate.today = function () {
        return CalendarDate.fromDate(new Date(), (0, utils_1.getLocalTimezone)());
    };
    /**
     * Get the start of the week
     */
    CalendarDate.prototype.startOfWeek = function () {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        var dayIndex = date.getUTCDay();
        if (dayIndex === 0) {
            return new CalendarDate(this.value);
        }
        return this.subtractDays(dayIndex);
    };
    /**
     * Subtract days and return a new CalendarDate.
     */
    CalendarDate.prototype.subtractDays = function (num) {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        var newDate = (0, date_fns_1.subHours)(date, num * 24);
        return CalendarDate.fromDate(newDate, utils_1.UTC_TIMEZONE);
    };
    /**
     * Add days and return a new CalendarDate
     */
    CalendarDate.prototype.addDays = function (num) {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        var newDate = (0, date_fns_1.addHours)(date, num * 24);
        return CalendarDate.fromDate(newDate, utils_1.UTC_TIMEZONE);
    };
    /**
     * Add months and return a new CalendarDate
     */
    CalendarDate.prototype.addMonths = function (num) {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        var newDate = (0, date_fns_1.addMonths)(date, num);
        return CalendarDate.fromDate(newDate, utils_1.UTC_TIMEZONE);
    };
    /**
     * Get the year as a number
     */
    CalendarDate.prototype.getYear = function () {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        return date.getUTCFullYear();
    };
    /**
     * Get the month as a number. Not the zero index.
     */
    CalendarDate.prototype.getMonth = function () {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        return date.getUTCMonth() + 1;
    };
    /**
     * Get the day of the month as a number.
     */
    CalendarDate.prototype.getDate = function () {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        return date.getUTCDate();
    };
    /**
     * Get the day of the week as a number
     */
    CalendarDate.prototype.getDayOfWeek = function () {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        return date.getUTCDay();
    };
    /**
     * Does this calendar date fall on a weekend?
     */
    CalendarDate.prototype.isWeekend = function () {
        var dayOfWeek = this.getDayOfWeek();
        return dayOfWeek === 0 || dayOfWeek === 6;
    };
    /**
     * Check the month using the index.
     */
    CalendarDate.prototype.isMonth = function (num) {
        return this.getMonth() === num;
    };
    /**
     * Are two CalendarDates the same month?
     */
    CalendarDate.prototype.isSameMonth = function (calendarDate) {
        return calendarDate.getMonth() === this.getMonth() && calendarDate.getYear() === this.getYear();
    };
    /**
     * Are two calendar dates the same date?
     */
    CalendarDate.prototype.isSameDate = function (calendarDate) {
        return this.toString() === calendarDate.toString();
    };
    /**
     * Is this calendar date before the date you pass in?
     */
    CalendarDate.prototype.isBefore = function (calendarDate) {
        return (0, date_fns_1.isBefore)(this.toDate(utils_1.UTC_TIMEZONE), calendarDate.toDate(utils_1.UTC_TIMEZONE));
    };
    /**
     * Is this calendar date after the date you pass in?
     */
    CalendarDate.prototype.isAfter = function (calendarDate) {
        return (0, date_fns_1.isAfter)(this.toDate(utils_1.UTC_TIMEZONE), calendarDate.toDate(utils_1.UTC_TIMEZONE));
    };
    /**
     * Get the raw DateString value.
     */
    CalendarDate.prototype.toString = function () {
        return this.value;
    };
    /**
     * Turn the calendar date into a Date. It requires a timezone because Dates always
     * include the time.
     */
    CalendarDate.prototype.toDate = function (timezone) {
        return (0, date_fns_tz_1.toDate)(this.value, {
            timeZone: timezone,
        });
    };
    /**
     * Format the calendar date using the standard formatting options. These will be localised
     * using the Intl API.
     */
    CalendarDate.prototype.format = function (options) {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        return (0, formats_1.formatDateWithStyle)(date, __assign(__assign({}, (options || { style: formats_1.DateFormatStyle.STANDARD })), { includeTime: false, timezone: utils_1.UTC_TIMEZONE }));
    };
    /**
     * Format a date string using a custom format string.
     */
    CalendarDate.prototype.inputFormat = function (format) {
        var date = this.toDate(utils_1.UTC_TIMEZONE);
        return (0, date_fns_tz_1.formatInTimeZone)(date, utils_1.UTC_TIMEZONE, format);
    };
    /**
     * Check if this calendar date is within the range of dates.
     */
    CalendarDate.prototype.check = function (options) {
        var _a = options || {}, minDate = _a.minDate, maxDate = _a.maxDate, _b = _a.allowWeekends, allowWeekends = _b === void 0 ? true : _b;
        if (minDate) {
            var isValid_1 = this.isAfter(minDate) || this.isSameDate(minDate);
            if (!isValid_1)
                return false;
        }
        if (maxDate) {
            var isValid_2 = this.isBefore(maxDate) || this.isSameDate(maxDate);
            if (!isValid_2)
                return false;
        }
        if (!allowWeekends && this.isWeekend()) {
            return false;
        }
        return true;
    };
    return CalendarDate;
}());
exports.CalendarDate = CalendarDate;
